import { useState } from 'react'
import { PRODUCT_ENTITLEMENTS } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { PageHeader } from '@/components/PageHeader'
import { NewRtaDialog } from '@/components/rtas/NewRtaDialog'
import { RtaTable } from '@/components/rtas/RtaTable'
import { Button } from '@/components/ui/button'
import { accountsQuery } from '@/lib/queries'
import { rtasQuery } from '@/lib/rta-queries'

/**
 * Every RTA across every account, pending first — the IA's landing page when they're running
 * several clients' alerts at once (the PRD's "overview across all assigned RTAs"). Clipbook
 * Internal, like the cross-account Reports table; each row opens the RTA, which then lives in its
 * own session tab with a pending chip.
 */
export const Route = createFileRoute('/app/rtas')({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(rtasQuery({})),
      context.queryClient.ensureQueryData(accountsQuery),
    ]),
  component: AllRtasPage,
})

function AllRtasPage() {
  const { data } = useSuspenseQuery(rtasQuery({}))
  const { data: accounts } = useSuspenseQuery(accountsQuery)
  const accountNames = new Map(accounts.accounts.map((account) => [account.id, account.name]))
  const [creating, setCreating] = useState(false)
  // Only accounts that bought RTAs: the UI gates everyone on the entitlement, staff included, so a
  // new RTA never lands somewhere its own page would redirect away from.
  const entitled = accounts.accounts
    .filter((account) => account.entitlements.includes(PRODUCT_ENTITLEMENTS.rta))
    .toSorted((a, b) => a.name.localeCompare(b.name))
  const rtas = data.rtas.toSorted(
    (a, b) => b.pendingCount - a.pendingCount || a.name.localeCompare(b.name),
  )
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="RTAs"
        description="Real-time alerts across every account, the ones with the most waiting first."
        className="mb-0"
        actions={
          <Button
            onClick={() => setCreating(true)}
            disabled={entitled.length === 0}
            title={entitled.length === 0 ? 'No account has RTAs switched on yet' : undefined}
          >
            New RTA
          </Button>
        }
      />
      {rtas.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No RTAs yet. Create one with the button above.
        </p>
      ) : (
        <RtaTable rtas={rtas} showClient showAccount accountNames={accountNames} />
      )}
      {creating && <NewRtaDialog accounts={entitled} onClose={() => setCreating(false)} />}
    </div>
  )
}
